import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiCheck, FiX, FiAlertCircle, FiUser, FiMapPin } = FiIcons;

const EmailAlerts = ({ userLocation }) => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      // In a real implementation, this would connect to a backend service
      // For now, we'll simulate the subscription process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate successful subscription
      setIsSubscribed(true);
      setSuccessMessage('Successfully subscribed to earthquake alerts!');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to subscribe. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    try {
      // Simulate unsubscribe process
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsSubscribed(false);
      setEmail('');
      setSuccessMessage('Successfully unsubscribed from alerts');
      
      // Reset message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError('Failed to unsubscribe. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-blue-500"
    >
      <div className="flex items-center space-x-2 mb-4">
        <SafeIcon icon={FiMail} className="text-blue-600 text-xl" />
        <h2 className="text-xl font-bold text-gray-900">Earthquake Email Alerts</h2>
      </div>
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <SafeIcon icon={FiCheck} className="text-green-600 mr-2" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <SafeIcon icon={FiAlertCircle} className="text-red-600 mr-2" />
          <span className="text-red-800">{error}</span>
        </div>
      )}
      
      {!isSubscribed ? (
        <div>
          <p className="text-gray-600 mb-4">
            Get notified via email when significant earthquakes occur in your area. 
            We'll send you real-time alerts with magnitude, location, and safety information.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
              </div>
            </div>
            
            {userLocation && (
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex items-center text-sm text-gray-600">
                  <SafeIcon icon={FiMapPin} className="mr-2" />
                  <span>Alerts will be sent for earthquakes near {userLocation}</span>
                </div>
              </div>
            )}
            
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Subscribe to Alerts'
                )}
              </button>
            </div>
          </form>
          
          <div className="mt-4 text-xs text-gray-500">
            <p>By subscribing, you agree to receive email alerts about earthquake activity. You can unsubscribe at any time.</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <div className="flex justify-center mb-3">
            <div className="bg-green-100 rounded-full p-3">
              <SafeIcon icon={FiCheck} className="text-green-600 text-2xl" />
            </div>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Subscribed!</h3>
          <p className="text-gray-600 mb-4">
            You're currently receiving earthquake alerts at <strong>{email}</strong>
          </p>
          <button
            onClick={handleUnsubscribe}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <SafeIcon icon={FiX} className="mr-2" />
                Unsubscribe
              </>
            )}
          </button>
        </div>
      )}
      
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Alert Settings</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SafeIcon icon={FiAlertCircle} className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">Magnitude Threshold</span>
            </div>
            <span className="text-sm font-medium text-gray-900">M5.0+</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SafeIcon icon={FiMapPin} className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">Radius</span>
            </div>
            <span className="text-sm font-medium text-gray-900">100 km</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <SafeIcon icon={FiUser} className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">Frequency</span>
            </div>
            <span className="text-sm font-medium text-gray-900">Immediate</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EmailAlerts;